/* UploadDiscoveryResultsVeilComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./UploadDiscoveryResultsVeilRenderer");

/**
 * UploadDiscoveryResultsVeilComponent
 *
 * Component for handling the UI veil for importing device discovery result files.
 * Provides drag-and-drop and file selector options, integrates with a renderer,
 * and manages context resolution and cleanup.
 *
 * @class
 * @extends EventEmitter
 * @listens UploadDiscoveryResultsVeilComponent#event:Shutdown
 * @fires UploadDiscoveryResultsVeilComponent#event:FileImported
 */
class UploadDiscoveryResultsVeilComponent extends EventEmitter {
    /**
     * @param {Object} logger - Logger instance for logging warnings/errors
     */
    constructor(logger) {
        super();
        this._logger = logger;
        this._titleText = "Import Discovery Result Files";
        this._renderer = new Renderer(this);
        this._registerListeners();
        this._renderer.render();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    /**
     * Register any listeners needed for this component.
     * Override to provide event subscriptions.
     * @private
     */
    _registerListeners() {}

    /**
     * Unregister listeners for this component.
     * Override to clean up event subscriptions.
     * @private
     */
    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    /**
     * Get the renderer instance for this component.
     * @returns {UploadDiscoveryResultsVeilRenderer}
     */
    get renderer() {
        return this.getRenderer();
    }

    /**
     * Retrieve the renderer instance.
     * @returns {UploadDiscoveryResultsVeilRenderer}
     */
    getRenderer() {
        return this._renderer;
    }

    /**
     * Get the title text for this veil component.
     * @returns {string}
     */
    get titleText() {
        return this._titleText;
    }

    /**
     * Get the logger for this component.
     * @returns {Object}
     */
    get logger() {
        return this._logger;
    }

    /**
     * Clean up and shutdown the component, unregistering listeners and shutting down the renderer.
     * @returns {Promise}
     */
    shutdown() {
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._logger = undefined;
                this._renderer = undefined;
                this._context = undefined;
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "UploadDiscoveryResultsVeilComponent.shutdown() error"
                );
            }
        );
    }

    /**
     * Handler for importing files, resolves the UI context.
     * @param {FileList|Array<File>} files - Files to import
     */
    importFile(files) {
        this._context.resolve(files);
    }

    /**
     * Handler to cancel the action, rejects the UI context.
     * @param {Error} [error] - Optional error to reject with.
     */
    cancelAction(error) {
        this._context.reject(error);
    }

    /**
     * Sets the UI context for resolving/rejecting actions (for use by framework).
     * @param {Object} context - UI context object with resolve/reject
     */
    set "$teve.ui.context"(context) {
        //jshint ignore:line
        this._context = context;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = UploadDiscoveryResultsVeilComponent;
